import { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import language from '../../variables/language';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import IconEdit from '../../components/icons/IconEdit';
import IconDelete from '../../components/icons/IconDelete';
import IconInfo from '../../components/icons/IconInfo';
import IconTag from '../../components/icons/IconTag';
import IconPencilTool from '../../components/icons/IconPencilTool';
import LessonCreateModal from './LessonCreateModal';
import EventEditModal from './EventEditModal';
import LessonDetailModal from './LessonDetailModal';
import LessonLinkChips from './LessonLinkChips';
import { getLessonTopicsBulk } from './workspace/lessonTopicsApi';
import { getLessonHomeworksBulk } from './lessonHomeworkApi';
import { resolveTopicCourseId } from '../courses/courseLevels';

// Same 'ua' -> 'uk' mapping DailySchedule.jsx/LessonsWithoutHomework.jsx need
// for real ICU month/weekday names out of Luxon.
const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };

const Events = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const label = 'events';
   const url = '/api/events';

   const [events, setEvents] = useState([]);
   // keyed by lesson id -> array of LessonTopic / LessonHomework (the latter
   // pre-filtered to only rows that actually carry an exerciseTemplate -
   // free-text-only homework isn't this column's concern, see .homework
   // below for that legacy field). One bulk fetch for the whole visible
   // list instead of one request per row.
   const [topicsByLesson, setTopicsByLesson] = useState({});
   const [homeworksByLesson, setHomeworksByLesson] = useState({});
   const [stats, setStats] = useState([]);
   const [search, setSearch] = useState('');
   const [dateFrom, setDateFrom] = useState('');
   const [dateTo, setDateTo] = useState('');
   const [isPaid, setIsPaid] = useState('');
   const [attendence, setAttendence] = useState('');
   const [toPay, setToPay] = useState('');
   const [refresh, setRefresh] = useState(0);
   const [createOpen, setCreateOpen] = useState(false);

   const [modalState, setModalState] = useState('none');
   const [toUpdateId, setToUpdateId] = useState('');
   const [lessonId, setLessonId] = useState('');

   const stateHundler = (state, itemId) => {
      if(state === 'edit'){
         setModalState('edit');
         setToUpdateId(itemId);
      }else if(state === 'menu'){
         setModalState('menu');
         setLessonId(itemId);
      }else if(state === 'none'){
         setModalState('none');
      }
   }
   const onRefreshHundler = () => {
      setModalState('none');
      setRefresh(prev => prev + 1);
   }

   const buildParams = () => {
      const params = new URLSearchParams();
      params.set('student', id);
      if(search.trim()) params.set('search', search.trim());
      if(dateFrom) params.set('dateFrom', dateFrom);
      if(dateTo) params.set('dateTo', dateTo);
      if(isPaid) params.set('isPaid', isPaid);
      if(attendence) params.set('attendence', attendence);
      if(toPay) params.set('toPay', toPay);
      return params.toString();
   }

   useEffect(() => {
      const qs = buildParams();
      axios.get(url + '?' + qs, { withCredentials: true })
         .then(response => setEvents(Array.isArray(response.data) ? response.data : []))
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
      axios.get(url + '/infopanel?' + qs, { withCredentials: true })
         .then(response => setStats(Array.isArray(response.data) ? response.data : []))
         .catch(() => setStats([]));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [id, search, dateFrom, dateTo, isPaid, attendence, toPay, refresh, navigate]);

   // Once we know which lessons are actually visible, fetch every one of
   // their topic/homework links in two bulk calls (see lessonTopicsApi.js/
   // lessonHomeworkApi.js's *Bulk helpers) rather than one request per row.
   useEffect(() => {
      const lessonIds = Array.from(new Set(events.map(e => e.lesson && e.lesson.id).filter(Boolean)));
      if(lessonIds.length === 0) {
         setTopicsByLesson({});
         setHomeworksByLesson({});
         return;
      }
      getLessonTopicsBulk(lessonIds).then(response => {
         const byLesson = {};
         (Array.isArray(response.data) ? response.data : []).forEach(lt => {
            if(!lt.lesson) return;
            const key = lt.lesson.id;
            if(!byLesson[key]) byLesson[key] = [];
            byLesson[key].push(lt);
         });
         setTopicsByLesson(byLesson);
      }).catch(() => setTopicsByLesson({}));
      getLessonHomeworksBulk(lessonIds).then(response => {
         const byLesson = {};
         (Array.isArray(response.data) ? response.data : []).forEach(hw => {
            if(!hw.lesson || !hw.exerciseTemplate) return;
            const key = hw.lesson.id;
            if(!byLesson[key]) byLesson[key] = [];
            byLesson[key].push(hw);
         });
         setHomeworksByLesson(byLesson);
      }).catch(() => setHomeworksByLesson({}));
   }, [events]);

   const openTopic = (topic) => {
      const courseId = resolveTopicCourseId(topic);
      if(!courseId) return;
      navigate('/students/' + id + '/workspace/' + courseId + '?topic=' + topic.id);
   }
   const openExercise = (exerciseTemplate) => {
      navigate('/exercise-templates/' + exerciseTemplate.id, { state: { returnTo: location.pathname } });
   }

   const refreshList = () => setRefresh(prev => prev + 1);
   const closeCreate = () => setCreateOpen(false);
   const onCreated = () => { closeCreate(); refreshList(); };

   const handleDelete = (e, event) => {
      e.stopPropagation();
      if(!window.confirm('Czy na pewno chcesz usunąć ten element?')) return;
      axios.delete(url + '/' + event.id, { withCredentials: true })
         .then(() => refreshList())
         .catch(err => console.log(err));
   }

   const lang = language[language.lang];
   const statValue = (name) => {
      const found = stats.find(s => s.name === name);
      return found ? found.value : '0';
   }
   const weekdayInfo = (dateStr) => {
      if(!dateStr) return null;
      const dt = DateTime.fromFormat(dateStr, 'yyyy-MM-dd');
      if(!dt.isValid) return null;
      return { label: lang.weekDaysShort[dt.weekday - 1], isWeekend: dt.weekday >= 6 };
   }
   const todayStr = DateTime.now().toFormat('yyyy-MM-dd');
   // some legacy lesson/event text columns store the literal strings "null"/"undefined"
   const cleanText = (val) => (val === 'null' || val === 'undefined' || val === null || val === undefined) ? '' : val;

   // month-group separators: a plain "yyyy-MM" key to detect when the month
   // actually changes between two consecutive rows (works regardless of
   // whether the list is sorted oldest-or-newest-first - a month change is
   // monotonic either way along an already date-sorted list), and a proper
   // localized "LLLL yyyy" label (e.g. "Вересень 2026") for the separator row.
   const monthKey = (dateStr) => (dateStr || '').slice(0, 7);
   const monthLabel = (dateStr) => {
      const dt = DateTime.fromFormat(dateStr || '', 'yyyy-MM-dd').setLocale(LUXON_LOCALE_BY_LANG[language.lang] || 'en');
      return dt.isValid ? dt.toFormat('LLLL yyyy') : dateStr;
   }

   // unset (0/null, never marked) renders nothing rather than a misleading
   // "No" - only an explicit 1 (yes, green) or 2 (no, red) shows a badge.
   const renderStatusBadge = (value) => {
      if(value !== 1 && value !== 2) {
         return '';
      }
      const isYes = value === 1;
      return (
         <span className={'student-lessons-badge' + (isYes ? ' on' : ' off-explicit')}>
            <i className='student-lessons-badge-dot'></i>{isYes ? lang.Yes : lang.No}
         </span>
      );
   }

   return (
      <div className='student-lessons'>
         <div className='student-lessons-toolbar'>
            <div className='student-lessons-stats'>
               <span className='student-lessons-stat-pill'><b>{statValue('Sum price')}</b><small>{lang['Sum price']}</small></span>
               <span className='student-lessons-stat-pill'><b>{statValue('Sum hours')}</b><small>{lang['Sum hours']}</small></span>
               <span className='student-lessons-stat-pill'><b>{statValue('Sum attendance')}</b><small>{lang['Sum attendance']}</small></span>
               <span className='student-lessons-stat-pill'><b>{statValue('Sum absence')}</b><small>{lang['Sum absence']}</small></span>
               <span className='student-lessons-stat-pill'><b>{statValue('Sum paid')}</b><small>{lang['Sum paid']}</small></span>
            </div>
            <div className='student-lessons-add-btn' onClick={() => setCreateOpen(true)} title={lang.modals[label].createTitle}>
               <IconAdd />
            </div>
         </div>

         <FilterBar
            search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }}
            filters={[
               {key:'dateFrom', type:'date', label: lang.dateFrom, value: dateFrom, onChange: setDateFrom},
               {key:'dateTo', type:'date', label: lang.dateTo, value: dateTo, onChange: setDateTo},
               {key:'isPaid', type:'select', label: lang.isPaid, options:[{id:1,name:'Yes'},{id:2,name:'No'}], value: isPaid, onChange: setIsPaid, numeric:true},
               {key:'attendence', type:'select', label: lang.attendence, options:[{id:1,name:'Yes'},{id:2,name:'No'}], value: attendence, onChange: setAttendence, numeric:true},
               {key:'toPay', type:'select', label: lang.toPay, options:[{id:1,name:'Yes'},{id:2,name:'No'}], value: toPay, onChange: setToPay, numeric:true},
            ]}
            resultCount={events.length} />

         <div className='student-lessons-table-wrap'>
            <table className='student-lessons-table'>
               <thead>
                  <tr>
                     <th>{lang.date}</th>
                     <th>{lang.isPaid}</th>
                     <th>{lang.attendence}</th>
                     <th>{lang.toPay}</th>
                     <th>{lang.topicColumn}</th>
                     <th>{lang.exerciseHomeworkColumn}</th>
                     <th>{lang.notes}</th>
                     <th></th>
                  </tr>
               </thead>
               <tbody>
                  {events.map((event, index) => {
                     const weekday = weekdayInfo(event.date);
                     const isToday = event.date === todayStr;
                     const isNewMonth = index === 0 || monthKey(event.date) !== monthKey(events[index - 1].date);
                     return (
                     <Fragment key={event.id}>
                     {isNewMonth ? (
                        <tr className='student-lessons-month-row'>
                           <td colSpan={8}>
                              <span className='student-lessons-month-label'>{monthLabel(event.date)}</span>
                           </td>
                        </tr>
                     ) : ''}
                     <tr className={'student-lessons-row' + (isToday ? ' student-lessons-row-today' : '')}
                        onClick={() => stateHundler('menu', event.lesson && event.lesson.id)}>
                        <td className='student-lessons-date-cell'>
                           {isToday ? <span className='student-lessons-today-marker' title={lang.today}></span> : ''}
                           <span className='student-lessons-date-main'>{event.date}</span>
                           <span className='student-lessons-time'>{event.start}{event.end ? '–' + event.end : ''}</span>
                           {weekday ? <span className={'student-lessons-weekday' + (weekday.isWeekend ? ' weekend' : '')}>{weekday.label}</span> : ''}
                        </td>
                        <td>{renderStatusBadge(event.isPaid)}</td>
                        <td>{renderStatusBadge(event.attendence)}</td>
                        <td>{renderStatusBadge(event.toPay)}</td>
                        <td className='student-lessons-text-cell'>
                           <LessonLinkChips icon={IconTag} items={
                              (event.lesson && topicsByLesson[event.lesson.id] || []).map(lt => ({
                                 id: lt.id, label: lt.topic.name, onClick: () => openTopic(lt.topic),
                              }))
                           } />
                        </td>
                        <td className='student-lessons-text-cell'>
                           <LessonLinkChips icon={IconPencilTool} items={
                              (event.lesson && homeworksByLesson[event.lesson.id] || []).map(hw => ({
                                 id: hw.id, label: hw.exerciseTemplate.name, onClick: () => openExercise(hw.exerciseTemplate),
                              }))
                           } />
                        </td>
                        <td className='student-lessons-text-cell'>{cleanText(event.notes)}</td>
                        <td className='student-lessons-actions-cell' onClick={e => e.stopPropagation()}>
                           <div className='student-lessons-icon-btn' onClick={() => stateHundler('menu', event.lesson && event.lesson.id)} title={lang.modals[label].updateTitle}>
                              <IconInfo style={{fill:'#8fc9c3', width:14, height:14, margin:0}} />
                           </div>
                           <div className='student-lessons-icon-btn' onClick={() => stateHundler('edit', event.id)}>
                              <IconEdit style={{fill:'#7ec2e8', width:14, height:14, margin:0}} />
                           </div>
                           <div className='student-lessons-icon-btn' onClick={(e) => handleDelete(e, event)}>
                              <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
                           </div>
                        </td>
                     </tr>
                     </Fragment>
                     );
                  })}
                  {events.length === 0 ? (
                     <tr className='student-lessons-empty-row'><td colSpan={8}>{lang['Empty list']}</td></tr>
                  ) : ''}
               </tbody>
            </table>
         </div>

         <EventEditModal isOpen={modalState === 'edit'} eventId={toUpdateId}
            onClose={() => stateHundler('none')} onSaved={onRefreshHundler} />

         <LessonDetailModal isOpen={modalState === 'menu'} lessonId={lessonId}
            onClose={() => stateHundler('none')} onSaved={onRefreshHundler}
            onParticipantsChanged={refreshList} />

         <LessonCreateModal isOpen={createOpen} studentId={id} onClose={closeCreate} onCreated={onCreated} />
      </div>
   );
}

export default Events;
